// @flow

export type AnimationManagerType = {
  start: () => void,
  cancel: () => void,
};

type AnimationManagerProp = {
  window: Object,
  duration: number,
  run: (progress: number, elapsedTime: number, elapsedTimeSinceLastRun: number) => void,
};

type CreateAnimationManagerType = (props: AnimationManagerProp) => AnimationManagerType;

// return timestamp in ms with precision up to μs
const getPreciseTimestamp =
  typeof window !== 'undefined' && window.performance
    ? () => window.performance.now()
    : () => Date.now();

export const createAnimationManager: CreateAnimationManagerType = ({ window, duration, run }) => {
  const startTime = getPreciseTimestamp();
  let previousElapsedTime = 0;
  let handle = null;
  const frame = () => {
    const elapsedTime = getPreciseTimestamp() - startTime;
    const elapsedTimeSinceLastRun = elapsedTime - previousElapsedTime;
    if (duration === Infinity) {
      run(0, elapsedTime, elapsedTimeSinceLastRun);
    } else {
      const progress = elapsedTime / duration;
      if (progress >= 1) {
        run(1, elapsedTime, elapsedTimeSinceLastRun);
        return;
      }
      run(progress, elapsedTime, elapsedTimeSinceLastRun);
    }
    previousElapsedTime = elapsedTime;
    handle = window.requestAnimationFrame(frame);
  };
  return {
    start: () => {
      handle = window.requestAnimationFrame(frame);
    },
    cancel: () => {
      if (handle) {
        window.cancelAnimationFrame(handle);
        handle = null;
      }
    },
  };
};

export default createAnimationManager;
